import { WidgetProps } from '@highimpact-front/remote-widget-core';
import { GenericErrorBoundary } from '@highimpact/dashboard-ui';
import { PermissionDecorator } from '@highimpact/react-decorators';
import EditSquareIcon from '@mui/icons-material/EditSquare';
import { forwardRef, useEffect, useRef, useState, type LegacyRef } from 'react';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { useDashboardHost } from '../../../../context/DashboardHostContext';
import { useDashboardPermissionScope } from '../../../../hooks/useDashboardPermissionScope';
import {
  useDashboardLibraryDispatch,
  useDashboardLibrarySelector,
} from '../../../../runtime/DashboardLibraryProvider';
import {
  makeEmptyText,
  setId,
  setLayout,
  setName,
  setTextValue as setElementTextValue,
} from '../../../../state/dashboard-element-operations';
import { setDeleteWidgetConfirmation, setTextValue } from '../../../../state/dashboard/slice';
import {
  findDashboardTypeById,
  findLayoutByElementId,
} from '../../../../state/store-operations';
import NameHandlerComponent from '../../../NameHandlerComponent';
import Widget from '../../commons/Widget';
import { WidgetDragHandle } from '../../commons/WidgetDragHandle';
import {
  WidgetToolbar,
  WidgetToolbarIconButton,
} from '../../commons/WidgetToolbar';
import { RichTextEditor } from './RichText';
import { useTextInstanceRegistration } from './useTextInstanceRegistration';
interface TextInputProps extends WidgetProps {
  name: string;
  value?: string;
  gridId: number;
  gridType: 'reports' | 'grids';
  useRichText?: boolean;
}

function Text(
  {
    name,
    className,
    non_removable,
    value = '',
    gridType,
    gridId,
    useRichText = true,
    ...props
  }: TextInputProps,
  ref: LegacyRef<HTMLDivElement>
) {
  const { dashboardElementId } = props;
  const dispatch = useDashboardLibraryDispatch();
  const host = useDashboardHost();
  const { site_id, project_id } = useDashboardPermissionScope();
  const [editorContent, setEditorContent] = useState(value);
  const [showToolbar, setShowToolbar] = useState(false);
  const dashboardType = useDashboardLibrarySelector((state) =>
    gridId ? findDashboardTypeById(gridId, state.dashboard) : undefined
  );
  const layout = useDashboardLibrarySelector((state) =>
    gridId && dashboardType
      ? findLayoutByElementId(
          props.dashboardElementId,
          state.dashboard[dashboardType][gridId]
        )
      : undefined
  );

  const valueSubject = useRef(new Subject<string>());

  useEffect(() => {
    const saveValue = async (value: string) => {
      const id = props.dashboardElementId;
      const data = makeEmptyText();
      const dataWithName = setName(name, data);
      const dataWithId = setId(id, dataWithName);
      const dataWithValue = setElementTextValue(value, dataWithId);
      const dataWithLayout = layout
        ? setLayout(layout, dataWithValue)
        : dataWithValue;
      await host.api.dashboard.putDashboardElement(id, dataWithLayout);
      if (gridId && dashboardType) {
        dispatch(
          setTextValue({
            widgetId: props.widget_id,
            gridId,
            gridType: dashboardType,
            value,
          })
        );
      }
    };
    const subscription = valueSubject.current
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe({
        next: (value) => saveValue(value),
        error: (err) => console.error('Error saving value:', err),
      });

    return () => subscription.unsubscribe();
  }, [
    dashboardType,
    dispatch,
    gridId,
    layout,
    name,
    props.dashboardElementId,
    props.widget_id,
    valueSubject,
  ]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    setEditorContent(newValue);
    valueSubject.current.next(newValue);
  };

  const handleRichTextChange = (newContent: string) => {
    setEditorContent(newContent);
    valueSubject.current.next(newContent);
  };

  const handleEditClick = () => {
    setShowToolbar(!showToolbar);
  };

  useTextInstanceRegistration({
    dashboardElementId,
    widgetId: props.widget_id,
    textValue: editorContent,
    enabled: true,
  });

  return (
    <Widget ref={ref} {...props} className={`text-input ${className || ''}`}>
      <GenericErrorBoundary>
        <PermissionDecorator
          item="dashboard_nav_bar_widget"
          project_id={project_id}
          site_id={site_id}
          extra={{ dashboard_id: gridId }}
        >
          <div className="widget-nav">
            <p style={{ display: 'flex' }}>
              {!props.dashboardOnlyMode && (
                <PermissionDecorator item="dashboard_move_widget">
                  <WidgetDragHandle />
                </PermissionDecorator>
              )}
              <PermissionDecorator item="dashboard_rename_widget">
                <NameHandlerComponent
                  widgetId={dashboardElementId}
                  dashboardElementId={props.widget_id}
                  dashboardId={gridId}
                  dashboardType={gridType}
                />
              </PermissionDecorator>
            </p>
            {!props.dashboardOnlyMode && (useRichText || !non_removable) && (
              <div className="nav-icon">
                <WidgetToolbar
                  dashboardElementId={dashboardElementId}
                  dashboardId={gridId}
                  // TODO: replace
                  platformActions={
                    !non_removable
                      ? {
                          delete: {
                            visible: true,
                            tooltipText: "Supprimer l'élément",
                            permissionItem: 'dashboard_delete_widget',
                            onClick: () =>
                              dispatch(
                                setDeleteWidgetConfirmation({
                                  state: 'open',
                                  dashboardElementId: dashboardElementId,
                                  widgetName: name,
                                })
                              ),
                          },
                        }
                      : undefined
                  }
                >
                  {useRichText && (
                    <WidgetToolbarIconButton
                      buttonIcon={EditSquareIcon}
                      tooltipText={
                        showToolbar
                          ? 'Masquer la toolbar'
                          : 'Afficher la toolbar'
                      }
                      onClick={handleEditClick}
                    />
                  )}
                </WidgetToolbar>
              </div>
            )}
          </div>
        </PermissionDecorator>
        <div className="widget-content">
          {useRichText ? (
            <RichTextEditor
              value={editorContent}
              onChange={handleRichTextChange}
              disabled={props.dashboardOnlyMode}
              placeholder="Veuillez remplir les informations demandées."
              showToolbar={showToolbar}
            />
          ) : (
            <textarea
              className="value"
              defaultValue={value}
              placeholder="Veuillez remplir les informations demandées."
              onChange={handleChange}
              disabled={props.dashboardOnlyMode}
            ></textarea>
          )}
        </div>
      </GenericErrorBoundary>
    </Widget>
  );
}

export default forwardRef(Text);
